import { LiveAnalysisConfig } from 'adapter/in/advanced-analytics/hooks/useFormGenerator';
import { AnalysisPermittedAction } from 'domain/entity/storedAnalysis';
import React, { createContext, ReactElement, useContext, useReducer } from 'react';

export enum ACTION_TYPE {
    SET_IS_UNSAVED = 'SET_IS_UNSAVED',
    SET_SAVED_ANALYSIS = 'SET_SAVED_ANALYSIS',
    SET_PARENT_FIELDS = 'SET_PARENT_FIELDS',
    SET_ANALYSIS_TITLE = 'SET_ANALYSIS_TITLE',
    SET_ANALYSIS_ID = 'SET_ANALYSIS_ID',
}

export type AnalyticsViewState = {
    isUnsaved: boolean;
    savedAnalysis: LiveAnalysisConfig;
    parentName: string;
    permittedActions: AnalysisPermittedAction;
    analysisTitle: string;
    originalParentName: string;
    analysisId: string;
};

export const initialState: AnalyticsViewState = {
    isUnsaved: false,
    savedAnalysis: null,
    parentName: '',
    permittedActions: {
        delete: false,
        duplicate: false,
        edit: false,
        share: false,
        transfer: false,
    },
    analysisTitle: '',
    originalParentName: '',
    analysisId: '',
};

export type SetHasAnalysisChanges = {
    type: ACTION_TYPE.SET_IS_UNSAVED;
    payload: boolean;
};

export type SetSavedAnalysis = {
    type: ACTION_TYPE.SET_SAVED_ANALYSIS;
    payload: LiveAnalysisConfig;
};

export type SetParentFields = {
    type: ACTION_TYPE.SET_PARENT_FIELDS;
    payload: { name?: string; permittedActions?: AnalysisPermittedAction; originalStoredAnalysisName?: string };
};

export type SetAnalysisTitle = {
    type: ACTION_TYPE.SET_ANALYSIS_TITLE;
    payload: string;
};

export type SetAnalysisId = {
    type: ACTION_TYPE.SET_ANALYSIS_ID;
    payload: string;
};

type Actions = SetHasAnalysisChanges | SetSavedAnalysis | SetParentFields | SetAnalysisTitle | SetAnalysisId;

export const useAnalysisViewStateReducer = (state: AnalyticsViewState, action: Actions): AnalyticsViewState => {
    const { type, payload } = action;
    switch (type) {
        case ACTION_TYPE.SET_IS_UNSAVED: {
            return {
                ...state,
                isUnsaved: payload,
            };
        }
        case ACTION_TYPE.SET_ANALYSIS_TITLE: {
            return {
                ...state,
                analysisTitle: payload,
            };
        }
        case ACTION_TYPE.SET_ANALYSIS_ID: {
            return {
                ...state,
                analysisId: payload,
            };
        }
        case ACTION_TYPE.SET_PARENT_FIELDS: {
            return {
                ...state,
                parentName: payload.name || state.parentName,
                permittedActions: payload.permittedActions || state.permittedActions,
                originalParentName: payload.originalStoredAnalysisName || state.originalParentName,
            };
        }
        case ACTION_TYPE.SET_SAVED_ANALYSIS:
        default: {
            return {
                ...state,
                savedAnalysis: payload,
            };
        }
    }
};

type AnalysisViewStateDispatch = {
    setIsUnsaved: (status: boolean) => void;
    setSavedAnalysis: (analysisConfig: LiveAnalysisConfig) => void;
    setParentFields: (payload: { name?: string; permittedActions?: AnalysisPermittedAction; originalName?: string }) => void;
    setAnalysisTitle: (title: string) => void;
    setAnalysisId: (id: string) => void;
};

export type AnalysisViewStateProviderProps = {
    children: ReactElement;
};

export const AnalysisViewStateDispatchContext = createContext({} as AnalysisViewStateDispatch);
const AnalysisViewStateContext = createContext({} as AnalyticsViewState);

const dispatchAnalysisView = (): AnalysisViewStateDispatch => useContext(AnalysisViewStateDispatchContext);
const useAnalysisView = (): AnalyticsViewState => useContext(AnalysisViewStateContext);

const AnalysisViewProvider = (props: AnalysisViewStateProviderProps): ReactElement => {
    const { children } = props;
    const [state, dispatch] = useReducer(useAnalysisViewStateReducer, initialState);

    const setIsUnsaved = (status: boolean) => {
        dispatch({ type: ACTION_TYPE.SET_IS_UNSAVED, payload: status });
    };

    const setSavedAnalysis = (analysisConfig: LiveAnalysisConfig) => {
        dispatch({ type: ACTION_TYPE.SET_SAVED_ANALYSIS, payload: analysisConfig });
    };

    const setParentFields = (payload: { name?: string; permittedActions?: AnalysisPermittedAction; originalName?: string }) => {
        dispatch({ type: ACTION_TYPE.SET_PARENT_FIELDS, payload });
    };

    const setAnalysisTitle = (payload: string) => {
        dispatch({ type: ACTION_TYPE.SET_ANALYSIS_TITLE, payload });
    };

    const setAnalysisId = (payload: string) => {
        dispatch({ type: ACTION_TYPE.SET_ANALYSIS_ID, payload });
    };

    return (
        <AnalysisViewStateContext.Provider value={state}>
            <AnalysisViewStateDispatchContext.Provider
                value={{
                    setIsUnsaved,
                    setSavedAnalysis,
                    setParentFields,
                    setAnalysisTitle,
                    setAnalysisId,
                }}
            >
                {children}
            </AnalysisViewStateDispatchContext.Provider>
        </AnalysisViewStateContext.Provider>
    );
};

export { AnalysisViewProvider, dispatchAnalysisView, useAnalysisView };
